import cx from "classnames";

import * as StateLib from "@/Utils/StateLib";
import ActionListItem from "../../Composites/ActionListItem/ActionListItem";
import { DuplicateView, MoveSystemTemplateToShared, RenameView } from "./ViewLibraryModal.util";
import { Flex, Icon, Text } from "@theorgchartdev/storybook";
import EditableTextWithFloatingControls from "@/appComponents/Blocks/Composites/EditableTextWithFloatingControls/EditableTextWithFloatingControls";
import { getFeatureAccess, getHoverTipContent } from "@/Utils/Misc";
import { GetViewName } from "../../Menus/ViewMenu/ViewMenu";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { VIEW_LIBRARY_OPT_PREFIX } from "@/Utils/Constants";
import { kAdminUser, enumChartTemplateSet, enumFeaturesList } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";

const BuildActions = (props, handlers) => {
  let addtHandlers = [];

  const isPromotableUser = kAdminUser === Globals._accountInfo.GetUserRole();

  const isPromotableSet = [
    enumChartTemplateSet.systemtemplates,
    enumChartTemplateSet.sharedtemplates
  ].includes(props.set);

  const hasPromoteFeature = getFeatureAccess(enumFeaturesList.promoteViews);

  if (isPromotableUser && isPromotableSet && hasPromoteFeature) {
    const isPromoted = props.manager.promoteMgr.getIsPromoted(props.id);

    if (isPromoted === false) {
      addtHandlers = [
        {
          dataTestId: `${VIEW_LIBRARY_OPT_PREFIX}-${props.set}-${props.id}-promote`,
          iconName: "plus",
          tooltipProps: getHoverTipContent("Promote View"),
          onClick: handlers.handlePromote
        }
      ];
    } else if (isPromoted) {
      addtHandlers = [
        {
          dataTestId: `${VIEW_LIBRARY_OPT_PREFIX}-${props.set}-${props.id}-demote`,
          iconName: "minus",
          tooltipProps: getHoverTipContent("Demote View"),
          onClick: handlers.handleDemote
        }
      ];
    }
  }

  if (props.set === enumChartTemplateSet.systemtemplates) {
    return [
      ...addtHandlers,
      {
        dataTestId: `${VIEW_LIBRARY_OPT_PREFIX}-${props.set}-${props.id}-copy-to-shared`,
        iconName: "file-export",
        tooltipProps: getHoverTipContent("Copy to Shared Library"),
        onClick: handlers.handleCopyToShared
      }
    ];
  }

  return [
    ...addtHandlers,
    {
      dataTestId: `${VIEW_LIBRARY_OPT_PREFIX}-${props.set}-${props.id}-duplicate`,
      iconName: "files",
      tooltipProps: { message: appstr("Duplicate view") },
      onClick: handlers.handleDuplicate
    },
    {
      dataTestId: `${VIEW_LIBRARY_OPT_PREFIX}-${props.set}-${props.id}-delete`,
      iconName: "trash-can",
      iconProps: { color: "error" },
      tooltipProps: { message: appstr("Delete view") },
      onClick: handlers.handleRemove
    }
  ];
};

const handleRename = (value, eventInfo) => {
  RenameView(eventInfo.id, eventInfo.set, value);
};

export const ViewListOption = props => {
  useWSMgrContext();
  const isEditing = props.manager.editingState.editingIds.has(props.id);

  const doc = StateLib.GetDoc();

  if (!doc) {
    return null;
  }

  const handlers = {
    handleDemote: () => {
      props.manager.promoteMgr.handleDemote(props.id);
    },
    handlePromote: () => {
      props.manager.promoteMgr.handlePromote(props.id);
    },
    handleCopyToShared: () => {
      MoveSystemTemplateToShared(props.id);
      props.manager.currentTabState.setCurrentTab(1);
    },
    handleDuplicate: () => {
      DuplicateView(props.id, props.set);
    },
    handleRemove: () => {
      props.manager.removeView(props.id, props.set);
    }
  };

  const activeTemplate = doc.GetActiveTemplate();
  const activeTemplateId = activeTemplate ? activeTemplate.GetTemplateID() : -1;
  const isActive = activeTemplateId === props.id;

  let contentAfter = null;
  if (isActive) {
    const isSelected = props.selectedTemplateId === props.id;
    const currentIconTextClass = "ocn-chart-explorer-current-text-wrapper";
    const selectedClass = isSelected ? "ocn-chart-explorer-active" : null;
    const labelModifier = isSelected ? "bold" : undefined;

    contentAfter = (
      <Flex className={cx(currentIconTextClass, selectedClass)} direction="row" alignItems="center">
        <Icon size={"small"} name={"eye"} />
        <Text modifier={labelModifier} size={"small"}>
          {appstr("Currently Viewing")}
        </Text>
      </Flex>
    );
  }

  const textContent = [enumChartTemplateSet.systemtemplates].includes(props.set) ? (
    <Text>{GetViewName(props.id)}</Text>
  ) : (
    <EditableTextWithFloatingControls
      editTooltipProps={{
        message: appstr("Rename view"),
        position: "top"
      }}
      className="oc-fill-width"
      size={"medium"}
      bold={false}
      text={GetViewName(props.id, isEditing)}
      onChange={handleRename}
      eventInfo={{ id: props.id, set: props.set }}
      align="right"
      onEditModeChange={editing =>
        props.manager.editingState.setEditingIds(prev => {
          const next = new Set(prev);
          editing ? next.add(props.id) : next.delete(props.id);
          return next;
        })
      }
      dataTestId={`${VIEW_LIBRARY_OPT_PREFIX}-${props.set}-${props.id}-name`}
      saveOnBlur
    />
  );

  return (
    <ActionListItem
      className="oc-view-library-tab-row"
      padding="12px 0"
      contentBefore={textContent}
      contentAfter={contentAfter}
      actions={BuildActions(props, handlers)}
      visible={isEditing ? "always" : "hover"}
    />
  );
};
